/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Trash2, Plus, Settings, Save, RotateCcw } from 'lucide-react';
import { useToast } from '../ui/use-toast';

/**
 * GradebookSettings Component
 *
 * Teacher interface for configuring gradebook settings:
 * - Assignment categories and weights
 * - Grading scale and display preferences
 * - Class-specific settings
 */
const GradebookSettings = ({ cohortId, onClose, onSettingsSaved }) => {
  const { toast } = useToast();

  // Default grading scales
  const defaultScales = {
    percentage: {
      'A': { min: 86, max: 100 },
      'B': { min: 73, max: 85 },
      'C+': { min: 67, max: 72 },
      'C': { min: 60, max: 66 },
      'C-': { min: 50, max: 59 },
      'I': { min: 0, max: 49 }
    },
    proficiency: {
      'Extending': { min: 90, max: 100 },
      'Proficient': { min: 60, max: 89 },
      'Developing': { min: 30, max: 59 },
      'Emerging': { min: 15, max: 29 },
      'IE': { min: 0, max: 14 }
    }
  };

  // State management
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form states
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryWeight, setNewCategoryWeight] = useState(10);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);

  // Load settings and categories on mount
  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, [cohortId]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${cohortId}/settings`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();

      // Ensure we have proper defaults
      const displayMode = data.display_mode || 'percentage';

      // Use saved grading scale if available, otherwise use default for the display mode
      const gradingScale = data.grading_scale || defaultScales[displayMode];

      console.log(`Loading settings with display_mode: ${displayMode}, using grading scale:`, gradingScale);

      setSettings({
        ...data,
        display_mode: displayMode,
        grading_scale: gradingScale
      });
    } catch (error) {
      console.error('Failed to fetch gradebook settings:', error);

      // Set defaults when fetch fails
      setSettings({
        display_mode: 'percentage',
        grading_scale: defaultScales.percentage,
        default_weights: {}
      });

      toast({
        title: "Using Default Settings",
        description: "Could not load existing settings, using defaults.",
        variant: "default"
      });
    }
  };

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories for cohort:', cohortId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${cohortId}/categories`, {
        credentials: 'include'
      });

      console.log('Categories API response status:', response.status);
      if (!response.ok) throw new Error('Failed to fetch categories');

      const data = await response.json();
      // Ensure weights are parsed as numbers
      const categoriesWithNumericWeights = data.map(cat => ({
        ...cat,
        weight: parseFloat(cat.weight) || 0
      }));

      // Debug: Log loaded categories with full details
      console.log('Loaded categories:', categoriesWithNumericWeights);
      console.table(categoriesWithNumericWeights.map(cat => ({
        id: cat.id,
        name: cat.name,
        weight: cat.weight,
        isTemp: cat.id.toString().startsWith('temp')
      })));

      setCategories(categoriesWithNumericWeights);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast({
        title: "Error",
        description: "Failed to load assignment categories",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      // Validate category weights sum to 100
      const totalWeight = categories.reduce((sum, cat) => sum + (parseFloat(cat.weight) || 0), 0);
      if (Math.abs(totalWeight - 100) > 0.01) {
        toast({
          title: "⚠️ Cannot Save Settings",
          description: `Category weights must equal exactly 100%. Current total: ${totalWeight.toFixed(1)}%. Please adjust your category weights.`,
          variant: "destructive",
          duration: 5000
        });
        return;
      }

      // Create weights object from categories
      const categoryWeights = {};
      categories.forEach(cat => {
        categoryWeights[cat.name] = cat.weight;
      });

      const updatedSettings = {
        ...settings,
        default_weights: categoryWeights,
        test_category: settings.test_category || 'Safety'
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${cohortId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedSettings)
      });

      if (!response.ok) throw new Error('Failed to save settings');

      // Clear unsaved changes and show success state
      setHasUnsavedChanges(false);
      setSaveSuccess(true);

      toast({
        title: "✅ Settings Saved Successfully!",
        description: "All gradebook settings have been updated and saved.",
        duration: 3000,
      });

      setSettings(updatedSettings);

      // Refresh gradebook data if callback provided
      if (onSettingsSaved) {
        onSettingsSaved();
      }

      // Close the dialog after successful save
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 2000); // Give user more time to see the success message
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: "Error",
        description: "Failed to save gradebook settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        title: "Invalid Name",
        description: "Category name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/cohort/${cohortId}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryName,
          weight: newCategoryWeight,
          sort_order: categories.length + 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create category');
      }

      const newCategory = await response.json();
      setCategories([...categories, newCategory]);
      setNewCategoryName('');
      setNewCategoryWeight(10);
      setShowAddCategory(false);

      toast({
        title: "Category Added",
        description: `"${newCategory.name}" category created successfully`,
      });
    } catch (error) {
      console.error('Failed to add category:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const updateCategoryWeight = async (categoryId, newWeight) => {
    try {
      const category = categories.find(c => c.id === categoryId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: category.name,
          weight: parseFloat(newWeight),
          sort_order: category.sort_order
        })
      });

      if (!response.ok) throw new Error('Failed to update category');

      const updatedCategory = await response.json();
      setCategories(categories.map(c =>
        c.id === categoryId ? updatedCategory : c
      ));
    } catch (error) {
      console.error('Failed to update category:', error);
      toast({
        title: "Error",
        description: "Failed to update category weight",
        variant: "destructive"
      });
    }
  };

  const deleteCategory = async (categoryId) => {
    // Prevent multiple deletions
    if (deletingCategoryId) return;

    // Find the category to delete
    const categoryToDelete = categories.find(c => c.id === categoryId);
    if (!categoryToDelete) return;

    // Debug: Log category information
    console.log('Attempting to delete category:', {
      categoryId,
      categoryToDelete,
      allCategories: categories
    });

    // Check if this is a temporary category that hasn't been saved yet
    if (categoryToDelete.id.toString().startsWith('temp') || !categoryToDelete.id || categoryToDelete.id === 'undefined') {
      toast({
        title: "Cannot Delete",
        description: "Please save your settings first before deleting categories",
        variant: "destructive"
      });
      return;
    }

    // Check if deleting this category would leave insufficient categories
    if (categories.length <= 1) {
      toast({
        title: "Cannot Delete",
        description: "You must have at least one category",
        variant: "destructive"
      });
      return;
    }

    // Calculate what the total weight would be after deletion
    const remainingWeight = getTotalWeight() - categoryToDelete.weight;

    // Warn if this would create an invalid weight total
    if (remainingWeight !== 100 && remainingWeight > 0) {
      const proceed = window.confirm(
        `Deleting "${categoryToDelete.name}" (${categoryToDelete.weight}%) will leave a total weight of ${remainingWeight}%. ` +
        `You'll need to adjust other category weights to reach 100%. Continue?`
      );
      if (!proceed) return;
    }

    setDeletingCategoryId(categoryId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();

        // Provide more specific error messages
        if (response.status === 403) {
          throw new Error('You do not have permission to delete this category. Make sure you are assigned to teach this class.');
        } else if (response.status === 400) {
          throw new Error(error.error || 'Cannot delete category with existing assignments');
        } else {
          throw new Error(error.error || 'Failed to delete category');
        }
      }

      setCategories(categories.filter(c => c.id !== categoryId));

      toast({
        title: "Category Deleted",
        description: `"${categoryToDelete.name}" has been removed successfully`,
      });
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });

      // If category not found, refresh the categories to sync with database
      if (error.message.includes('Category not found')) {
        console.log('Refreshing categories due to 404 error...');
        fetchCategories();
      }
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const getTotalWeight = () => {
    return categories.reduce((sum, cat) => sum + (parseFloat(cat.weight) || 0), 0);
  };

  // Handle grading mode change (no longer saves immediately)
  const handleGradingModeChange = (newMode) => {
    const newScale = defaultScales[newMode];
    setSettings({
      ...settings,
      display_mode: newMode,
      grading_scale: newScale
    });

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    toast({
      title: "Grading Mode Changed",
      description: `Switched to ${newMode === 'percentage' ? 'Percentage & Letter Grades' : 'Proficiency Levels'}. Remember to save your settings!`,
      duration: 3000
    });
  };

  // Calculate dynamic ranges for display
  const calculateGradeRange = (gradeLetter, currentScale) => {
    const grades = Object.entries(currentScale).sort((a, b) => b[1].min - a[1].min);
    const currentIndex = grades.findIndex(([letter]) => letter === gradeLetter);

    if (currentIndex === -1) return { min: 0, max: 100 };

    const currentGrade = grades[currentIndex];
    const min = currentGrade[1].min;

    // Max is either 100 (for highest grade) or one less than the next grade's min
    let max;
    if (currentIndex === 0) {
      max = 100; // Highest grade goes to 100%
    } else {
      const nextGrade = grades[currentIndex - 1];
      max = nextGrade[1].min - 1;
    }

    return { min, max };
  };

  const resetToDefaults = () => {
    setCategories([
      { id: 'temp1', name: 'Safety', weight: 25, sort_order: 1 },
      { id: 'temp2', name: 'Projects', weight: 50, sort_order: 2 },
      { id: 'temp3', name: 'Classwork', weight: 25, sort_order: 3 }
    ]);
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gradebook Settings
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading settings...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Gradebook Settings
            {hasUnsavedChanges && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                Unsaved Changes
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="categories" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="categories">Categories & Weights</TabsTrigger>
            <TabsTrigger value="grading">Grading Scale</TabsTrigger>
            <TabsTrigger value="display">Display Options</TabsTrigger>
          </TabsList>

          {/* Categories & Weights Tab */}
          <TabsContent value="categories" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Assignment Categories
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={resetToDefaults}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Defaults
                    </Button>
                    <Button size="sm" onClick={() => setShowAddCategory(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categories.map((category) => (
                    <div key={category.id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{category.name}</div>
                        <div className="text-sm text-gray-500">
                          Sort order: {category.sort_order}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`weight-${category.id}`} className="text-sm">
                          Weight:
                        </Label>
                        <Input
                          id={`weight-${category.id}`}
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={category.weight}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const newWeight = parseFloat(e.target.value) || 0;
                            setCategories(categories.map(c =>
                              c.id === category.id ? { ...c, weight: newWeight } : c
                            ));
                            setHasUnsavedChanges(true);
                          }}
                          onBlur={(e) => {
                            if (category.id.toString().startsWith('temp')) return;
                            updateCategoryWeight(category.id, parseFloat(e.target.value) || 0);
                          }}
                          className="w-20"
                        />
                        <span className="text-sm">%</span>
                        {categories.length > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteCategory(category.id)}
                            disabled={
                              category.id.toString().startsWith('temp') ||
                              categories.length <= 1 ||
                              deletingCategoryId === category.id ||
                              !category.id || category.id === 'undefined'
                            }
                            title={
                              category.id.toString().startsWith('temp')
                                ? "Save settings first to delete categories"
                                : categories.length <= 1
                                ? "Cannot delete the last category"
                                : deletingCategoryId === category.id
                                ? "Deleting..."
                                : "Delete this category"
                            }
                          >
                            {deletingCategoryId === category.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">Total Weight:</span>
                    <Badge variant={getTotalWeight() === 100 ? "default" : "destructive"}>
                      {getTotalWeight()}%
                    </Badge>
                  </div>

                  {getTotalWeight() !== 100 && (
                    <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                      ⚠️ Category weights must sum to exactly 100%
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Grading Scale Tab */}
          <TabsContent value="grading" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>
                    {settings?.display_mode === 'proficiency' ? 'Proficiency Scale' : 'Letter Grade Scale'}
                  </span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="grading-mode" className="text-sm font-medium">
                        Grading Mode:
                      </Label>
                      <Select
                        value={settings?.display_mode || 'percentage'}
                        onValueChange={handleGradingModeChange}
                      >
                        <SelectTrigger
                          className="w-48"
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent onClick={(e) => e.stopPropagation()}>
                          <SelectItem value="percentage">Percentage & Letter Grades</SelectItem>
                          <SelectItem value="proficiency">Proficiency Levels</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {settings?.grading_scale && Object.entries(settings.grading_scale).map(([letter, range]) => (
                    <div key={letter} className="space-y-2">
                      <Label className="text-center block font-medium text-lg">{letter}</Label>
                      <div className="space-y-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={range.min}
                          onFocus={(e) => {
                            e.stopPropagation();
                            e.target.select();
                          }}
                          onChange={(e) => {
                            e.stopPropagation(); // Prevent dialog from closing
                            const newScale = { ...settings.grading_scale };
                            newScale[letter].min = parseInt(e.target.value) || 0;
                            setSettings({ ...settings, grading_scale: newScale });
                            setHasUnsavedChanges(true);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-center"
                        />
                        <div className="text-center text-xs text-gray-500">
                          {(() => {
                            const dynamicRange = calculateGradeRange(letter, settings.grading_scale);
                            return `${dynamicRange.min}% - ${dynamicRange.max}%`;
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    💡 <strong>Tip:</strong> Grade ranges are automatically calculated. For example, if A starts at 90%,
                    it covers 90-100%. The next grade (B) will cover from its minimum up to 89%.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Display Options Tab */}
          <TabsContent value="display" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Display Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Round Final Grades</Label>
                    <p className="text-sm text-gray-600">
                      Round final grades to whole numbers
                    </p>
                  </div>
                  <Switch
                    checked={settings?.round_final_grades || false}
                    onCheckedChange={(checked) => {
                      setSettings({ ...settings, round_final_grades: checked });
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Show Points</Label>
                    <p className="text-sm text-gray-600">
                      Display points earned alongside percentages
                    </p>
                  </div>
                  <Switch
                    checked={settings?.show_points || false}
                    onCheckedChange={(checked) => {
                      setSettings({ ...settings, show_points: checked });
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Show Letter Grades</Label>
                    <p className="text-sm text-gray-600">
                      Display letter grades in gradebook
                    </p>
                  </div>
                  <Switch
                    checked={settings?.show_letter_grades || false}
                    onCheckedChange={(checked) => {
                      setSettings({ ...settings, show_letter_grades: checked });
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>

                {/* Late Penalty Section - Enhanced */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        📅 Late Submission Policy
                      </Label>
                      <p className="text-sm text-gray-600">
                        Configure how late assignments are handled and penalized
                      </p>
                    </div>
                    <Switch
                      checked={settings?.allow_late_submissions || false}
                      onCheckedChange={(checked) => {
                        setSettings({ ...settings, allow_late_submissions: checked });
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>

                  {settings?.allow_late_submissions && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-medium">Default Late Penalty</Label>
                          <p className="text-sm text-gray-600">
                            Percentage deducted from assignment grade for late submission
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="5"
                            value={settings?.default_late_penalty || 10}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSettings({
                                ...settings,
                                default_late_penalty: parseInt(e.target.value) || 0
                              });
                              setHasUnsavedChanges(true);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-20"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-medium">Grace Period</Label>
                          <p className="text-sm text-gray-600">
                            Minutes after due date before penalty applies
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="1440"
                            step="15"
                            value={settings?.late_grace_period || 0}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSettings({
                                ...settings,
                                late_grace_period: parseInt(e.target.value) || 0
                              });
                              setHasUnsavedChanges(true);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-20"
                          />
                          <span className="text-sm">min</span>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                        <strong>Example:</strong> With a {settings?.default_late_penalty || 10}% penalty and {settings?.late_grace_period || 0} minute grace period,
                        a student earning 85% on a late assignment would receive {Math.max(0, 85 - (settings?.default_late_penalty || 10))}%.
                      </div>
                    </div>
                  )}
                </div>

                {/* Test Integration Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        🧪 Safety Test Integration
                      </Label>
                      <p className="text-sm text-gray-600">
                        Configure how safety test scores are automatically added to the gradebook
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 pl-4 border-l-2 border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">Test Category</Label>
                        <p className="text-sm text-gray-600">
                          Choose which category safety test scores should be placed into
                        </p>
                      </div>
                      <Select
                        value={settings?.test_category || 'Safety'}
                        onValueChange={(value) => {
                          setSettings({ ...settings, test_category: value });
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger
                          className="w-48"
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent onClick={(e) => e.stopPropagation()}>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.name}>
                              {category.name} ({category.weight}%)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="text-xs text-gray-500 bg-green-50 p-3 rounded">
                      <strong>Note:</strong> When students complete safety tests, their highest score will automatically be recorded in the selected category.
                      If a student retakes a test, only their best score will be kept.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={saveSettings}
            disabled={saving || getTotalWeight() !== 100}
            className={`
              ${saveSuccess ? 'bg-green-600 hover:bg-green-700' : ''}
              ${hasUnsavedChanges ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
            `}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : saveSuccess ? (
              <>
                <span className="h-4 w-4 mr-2">✅</span>
                Settings Saved!
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {hasUnsavedChanges ? 'Save Changes' : 'Save Settings'}
              </>
            )}
          </Button>
        </DialogFooter>

        {/* Add Category Dialog */}
        <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Assignment Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="category-name">Category Name</Label>
                <Input
                  id="category-name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Quizzes, Presentations"
                />
              </div>
              <div>
                <Label htmlFor="category-weight">Weight (%)</Label>
                <Input
                  id="category-weight"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={newCategoryWeight}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setNewCategoryWeight(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCategory(false)}>
                Cancel
              </Button>
              <Button onClick={addCategory}>
                Add Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default GradebookSettings;